import type { PortalScope } from '@core/component/ScopedPortal';
import { isNativeMobilePlatform } from '@core/mobile/isNativeMobilePlatform';
import type { EditorType } from '@lexical-core';
import type { HistoryItem } from '@queries/history/types';
import { onElementConnect } from '@solid-primitives/lifecycle';
import { isIOS } from '@solid-primitives/platform';
import { cn } from '@ui';
import {
  COMMAND_PRIORITY_CRITICAL,
  COMMAND_PRIORITY_HIGH,
  KEY_ENTER_COMMAND,
  KEY_ESCAPE_COMMAND,
  KEY_TAB_COMMAND,
  type LexicalEditor,
} from 'lexical';
import {
  type Accessor,
  createEffect,
  createSignal,
  type JSX,
  on,
  onCleanup,
  Show,
} from 'solid-js';
import type { SetStoreFunction } from 'solid-js/store';
import { FloatingMenuGroup } from '../../context/FloatingMenuContext';
import {
  createLexicalWrapper,
  LexicalWrapperContext,
} from '../../context/LexicalWrapperContext';
import {
  autoRegister,
  codePlugin,
  createAccessoryStore,
  customSelectionDataPlugin,
  emojisPlugin,
  filePastePlugin,
  type ItemMention,
  iosCursorScrollPlugin,
  keyboardFocusPlugin,
  mediaPlugin,
  mentionsPlugin,
  type SelectionData,
  selectionDataPlugin,
  tabIndentationPlugin,
  textPastePlugin,
} from '../../plugins';
import { checkboxToTaskPlugin } from '../../plugins/checkbox-to-task';
import { restoreFocusPlugin } from '../../plugins/restore-focus';
import { createMenuOperations } from '../../shared/inlineMenu';
import {
  editorIsEmpty,
  initializeEditorEmpty,
  setEditorStateFromHtml,
  setEditorStateFromMarkdown,
} from '../../utils';
import type { UserMentionRecord } from '../../utils/mentionsUtils';
import { EmojiMenu } from '../menu/EmojiMenu';
import { FloatingLinkMenu } from '../menu/FloatingLinkMenu';
import { MentionsMenu } from '../menu/MentionsMenu';
import { DecoratorRenderer } from './DecoratorRenderer';
import { NodeAccessoryRenderer } from './NodeAccessoryRenderer';

/**
 * @param editable - A signal that indicates whether the textarea is editable
 * @param onChange - A callback function that is called when the textarea value changes.
 *     The current markdown text is passed as an argument.
 * @param initialValue - The initial markdown text to display in the textarea.
 * @param placeholder - The placeholder text to display in the textarea.
 * @param type - The type of editor to use. Defaults to 'markdown'. Could aslo pass chat to turn off headings.
 * @param onEnter - A callback function that is called when the user presses Enter in the textarea.
 *     If the function returns true, the enter press will not propagate to the lexical editor.
 * @param onEscape - A callback function that is called when the user presses Escape in the textarea. If the function
 *     returns true Lexical's default behavior will be prevented.
 */
interface MarkdownTextareaProps {
  editable: Accessor<boolean>;
  onChange?: (value: string, editor?: LexicalEditor) => void;
  initialValue?: string;
  initialHtml?: string;
  placeholder?: string;
  watermark?: JSX.Element;
  type?: EditorType;
  onEnter?: (e: KeyboardEvent, value: string) => boolean;
  focusOnMount?: boolean;
  class?: string;
  anchor?: HTMLElement | null;
  onUserMention?: (mention: UserMentionRecord) => void;
  onRemoveMention?: (mention: ItemMention) => void;
  portalScope?: PortalScope;
  useBlockBoundary?: boolean;
  onDocumentMention?: (mention: HistoryItem) => void;
  onEscape?: (e: KeyboardEvent) => boolean;
  onTab?: (e: KeyboardEvent) => boolean;
  captureEditor?: (editor: LexicalEditor) => void;
  onFocusReady?: (focusFn: () => void) => void;
  onFocusLeaveStart?: (e: KeyboardEvent) => void;
  onFocusLeaveEnd?: (e: KeyboardEvent) => void;
  formatState?: SelectionData;
  setFormatState?: SetStoreFunction<SelectionData>;
  domRef?: (ref: HTMLDivElement) => void | HTMLDivElement;
  scrollRef?: Accessor<HTMLElement | undefined>;
  onPasteFilesAndDirs?: (
    files: FileSystemFileEntry[],
    directories: FileSystemDirectoryEntry[]
  ) => void;
}

export function MarkdownTextarea(props: MarkdownTextareaProps) {
  let mountRef!: HTMLDivElement;
  let scrollContainerRef: HTMLDivElement | undefined;
  const lexicalWrapper = createLexicalWrapper({
    type: props.type ?? 'markdown',
    namespace: 'markdown-textarea',
    isInteractable: props.editable,
  });
  const { editor, plugins, cleanup: cleanupLexical } = lexicalWrapper;

  if (props.captureEditor) {
    props.captureEditor(editor);
  }

  if (props.onFocusReady) {
    props.onFocusReady(() => {
      if (editor.getRootElement()) editor.focus();
    });
  }

  const [markdownState, setMarkdownState] = createSignal<string>('');

  let didInitializeContent = false;

  const onConnect = () => {
    if (props.focusOnMount) {
      setTimeout(() => {
        mountRef.focus();
      });
    }

    if (props.initialHtml) {
      setEditorStateFromHtml(editor, props.initialHtml);
    } else if (props.initialValue) {
      setEditorStateFromMarkdown(editor, props.initialValue);
    }

    if (props.initialHtml || props.initialValue) {
      // We do this to make sure anything relying on the markdownState existing initially
      // has the content
      props.onChange?.(markdownState());
    }

    didInitializeContent = true;
  };

  createEffect(() => {
    editor.setEditable(props.editable());
  });

  createEffect(
    on(
      markdownState,
      () => {
        if (!didInitializeContent) return;
        props.onChange?.(markdownState(), editor);
      },
      { defer: true }
    )
  );

  if (props.initialHtml) {
    setEditorStateFromHtml(editor, props.initialHtml);
  } else if (props.initialValue) {
    setEditorStateFromMarkdown(editor, props.initialValue);
  } else {
    initializeEditorEmpty(editor);
  }

  const [showPlaceholder, setShowPlaceholder] = createSignal(true);

  const mentionsMenuOperations = createMenuOperations();
  const emojisMenuOperations = createMenuOperations();

  plugins
    .richText()
    .list()
    .markdownShortcuts()
    .delete()
    .state<string>(setMarkdownState, 'markdown')
    .history(400)
    .use(restoreFocusPlugin())
    .use(checkboxToTaskPlugin())
    .use(mediaPlugin())
    .use(
      props.formatState && props.setFormatState
        ? customSelectionDataPlugin(
            lexicalWrapper,
            props.formatState,
            props.setFormatState
          )
        : selectionDataPlugin(lexicalWrapper)
    )
    .use(tabIndentationPlugin())
    .use(textPastePlugin())
    .use(
      mentionsPlugin({
        menu: mentionsMenuOperations,
        onRemoveMention: props.onRemoveMention,
      })
    )
    .use(emojisPlugin({ menu: emojisMenuOperations }));

  if (props.onPasteFilesAndDirs) {
    plugins.use(
      filePastePlugin({
        onPasteFilesAndDirs: props.onPasteFilesAndDirs,
      })
    );
  }

  if (isIOS || isNativeMobilePlatform()) {
    plugins.use(
      iosCursorScrollPlugin({
        scrollContainer: props.scrollRef ?? (() => scrollContainerRef),
      })
    );
  }

  const [accessoryStore, setAccessoryStore] = createAccessoryStore();
  plugins.use(
    codePlugin({
      setAccessories: setAccessoryStore,
      accessories: accessoryStore,
    })
  );

  plugins.useReactive(
    [() => props.onFocusLeaveEnd, () => props.onFocusLeaveStart],
    () => {
      if (props.onFocusLeaveEnd && props.onFocusLeaveStart) {
        return keyboardFocusPlugin({
          onFocusLeaveStart: props.onFocusLeaveStart,
          onFocusLeaveEnd: props.onFocusLeaveEnd,
          ignoreKeys: () =>
            mentionsMenuOperations.isOpen() || emojisMenuOperations.isOpen(),
        });
      }
    }
  );

  let cleanupEnterListener: () => void = () => {};
  createEffect(() => {
    cleanupEnterListener();
    const onEnter = props.onEnter;
    if (onEnter == null) return;
    cleanupEnterListener = editor.registerCommand(
      KEY_ENTER_COMMAND,
      (e) => {
        if (!e) return false;
        // TODO (seamus) : This is hacky. If we got a props.onEnter,then shift+enter becomes
        // the new "regular enter", so we delete the shiftKey and pass along to lexical.
        if (e.altKey && e.shiftKey) {
          Object.defineProperty(e, 'altKey', { value: false });
          return false;
        }

        if (e.shiftKey) {
          Object.defineProperty(e, 'shiftKey', { value: false });
          return false;
        }

        const captured = onEnter(e, markdownState());
        if (captured) {
          e.preventDefault();
          e.stopPropagation();
        }
        return captured;
      },
      // Run at HIGH here so that the mentions menu can run at CRITICAL
      COMMAND_PRIORITY_HIGH
    );
  });

  onCleanup(() => {
    cleanupEnterListener();
    cleanupLexical();
  });

  createEffect(() => {
    markdownState();
    setShowPlaceholder(editorIsEmpty(editor));
  });

  autoRegister(
    editor.registerCommand(
      KEY_ESCAPE_COMMAND,
      (e) => (props.onEscape ? props.onEscape(e) : false),
      COMMAND_PRIORITY_CRITICAL
    ),
    editor.registerCommand(
      KEY_TAB_COMMAND,
      (e) => (props.onTab ? props.onTab(e) : false),
      COMMAND_PRIORITY_CRITICAL
    )
  );

  return (
    <LexicalWrapperContext.Provider value={lexicalWrapper}>
      <div
        ref={scrollContainerRef}
        class={cn('relative size-full overflow-auto min-h-8', props.class)}
        on:keydown={(e) => {
          e.stopPropagation();
        }}
        on:click={(e) => {
          e.stopPropagation();
          editor.focus();
        }}
        on:mousedown={(e) => {
          e.stopPropagation();
        }}
        on:mouseup={(e) => {
          e.stopPropagation();
        }}
      >
        <div
          ref={(el) => {
            onElementConnect(el, () => {
              editor.setRootElement(el);
              props.domRef?.(el);
              mountRef = el;
              onConnect();
            });
          }}
          contentEditable={props.editable()}
        />

        <DecoratorRenderer editor={editor} />
        <NodeAccessoryRenderer editor={editor} store={accessoryStore} />
        <Show when={showPlaceholder()}>
          <div class="pointer-events-none text-ink-placeholder absolute top-0">
            <p class="pointer-events-none">{props.placeholder ?? '...'}</p>
          </div>
        </Show>
        <Show when={props.watermark}>
          <div class="text-ink/50 mt-[1lh]" data-watermark>
            {props.watermark}
          </div>
        </Show>

        <MentionsMenu
          editor={editor}
          menu={mentionsMenuOperations}
          anchor={props.anchor}
          onUserMention={props.onUserMention}
          onDocumentMention={props.onDocumentMention}
          useBlockBoundary={props.useBlockBoundary}
          portalScope={props.portalScope}
        />
        <EmojiMenu
          editor={editor}
          menu={emojisMenuOperations}
          portalScope={props.portalScope}
          useBlockBoundary={props.useBlockBoundary}
        />
        <FloatingMenuGroup>
          <FloatingLinkMenu />
        </FloatingMenuGroup>
      </div>
    </LexicalWrapperContext.Provider>
  );
}
